import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Header.css';

export const Header = ({ currentPage, onThemeToggle }) => {
  const { logout, user, userEmail, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [profileOpen, setProfileOpen] = React.useState(false);

  // Use displayName from Graph profile, fallback to email-derived or "User"
  const displayName = user?.displayName || (userEmail && userEmail.includes('@') 
    ? userEmail.split('@')[0] 
    : userEmail || 'User');

  // Show actual email - prefer user email that looks like a real email (not GUID UPN)
  const displayEmail = (() => {
    const email = user?.email || userEmail || '';
    // If it looks like a GUID UPN, don't show it - show the sign-in identity instead
    if (/^[0-9a-f]{8}-/.test(email)) {
      return user?.signInEmail || displayName;
    }
    return email;
  })();

  const handleSignOut = () => {
    logout();
  };

  return (
    <header className="nav-bar" role="banner">
      <div className="nav-brand-container">
        <a className="nav-brand" href="/" onClick={(e) => { e.preventDefault(); navigate('/briefing/01'); }}>🌴 Goa Resort Investment</a>
      </div>

      <nav className="nav-links" aria-label="Briefing pages">
        {['01','02','03','04','05','06','07','08','09','10','11'].map(num => (
          <a
            key={num}
            href={`/briefing/${num}`}
            className={currentPage === num ? 'nav-active' : ''}
            onClick={(e) => { e.preventDefault(); navigate(`/briefing/${num}`); }}
          >{num}</a>
        ))}
        {isAdmin && <a href="/admin" onClick={(e) => { e.preventDefault(); navigate('/admin'); }} className="nav-admin-link">Admin</a>}
      </nav>

      <div className="nav-actions">
        <button
          className="theme-toggle"
          type="button"
          aria-label="Toggle theme"
          onClick={onThemeToggle}
        />

        {userEmail && (
          <div className="user-menu" style={{ position: 'relative' }}>
            <button
              className="profile-button"
              onClick={() => setProfileOpen(!profileOpen)}
              title={userEmail}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: 'rgba(255,255,255,0.15)',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '20px',
                padding: '0.35rem 0.75rem',
                color: 'white',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: 500,
              }}
            >
              <span style={{
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #0891b2, #0e7490)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.8rem',
                fontWeight: 700,
              }}>
                {(displayName || 'U')[0].toUpperCase()}
              </span>
              <span className="user-name">{displayName}</span>
              <span style={{ fontSize: '0.6rem' }}>▼</span>
            </button>
            {profileOpen && (
              <div
                className="profile-dropdown"
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '0.4rem',
                  background: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  minWidth: '180px',
                  zIndex: 1000,
                  overflow: 'hidden',
                }}
              >
                <div style={{
                  padding: '0.75rem 1rem',
                  borderBottom: '1px solid #eee',
                }}>
                  <div style={{ fontSize: '0.9rem', color: '#333', fontWeight: 600 }}>
                    {displayName}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '0.2rem' }}>
                    {displayEmail}
                  </div>
                </div>
                <button
                  onClick={() => { setProfileOpen(false); navigate('/profile'); }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    color: '#333',
                    textAlign: 'left',
                  }}
                  onMouseOver={e => e.currentTarget.style.background = '#f5f5f5'}
                  onMouseOut={e => e.currentTarget.style.background = 'none'}
                >
                  👤 Profile
                </button>
                <button
                  onClick={() => { setProfileOpen(false); handleSignOut(); }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    color: '#d32f2f',
                    textAlign: 'left',
                  }}
                  onMouseOver={e => e.currentTarget.style.background = '#f5f5f5'}
                  onMouseOut={e => e.currentTarget.style.background = 'none'}
                >
                  🚪 Sign Out
                </button>
              </div>
            )}
          </div>
        )}

        <button
          className="nav-hamburger"
          type="button"
          aria-label="Open menu"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          ☰
        </button>
      </div>

      {mobileMenuOpen && (
        <aside className="mobile-nav" role="dialog" aria-label="Navigation">
          <button
            className="close"
            aria-label="Close menu"
            onClick={() => setMobileMenuOpen(false)}
          >
            ×
          </button>
          <a href="/briefing/01" onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); navigate('/briefing/01'); }}>🏠 Home</a>
          <a href="/briefing/01" onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); navigate('/briefing/01'); }}>01 — Executive Summary</a>
          <a href="/briefing/02" onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); navigate('/briefing/02'); }}>02 — Investment Foundation</a>
          <a href="/briefing/03" onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); navigate('/briefing/03'); }}>03 — Legal / Tax / Financial</a>
          <a href="/briefing/04" onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); navigate('/briefing/04'); }}>04 — Operations & Brand</a>
          <a href="/briefing/05" onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); navigate('/briefing/05'); }}>05 — Five-Family Governance</a>
          <a href="/briefing/06" onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); navigate('/briefing/06'); }}>06 — Retirement Livability</a>
          <a href="/briefing/07" onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); navigate('/briefing/07'); }}>07 — Risk Premortem</a>
          <a href="/briefing/08" onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); navigate('/briefing/08'); }}>08 — Recommendation & Next Steps</a>
          <a href="/briefing/09" onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); navigate('/briefing/09'); }}>09 — Commercial Simulator</a>
          <a href="/briefing/10" onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); navigate('/briefing/10'); }}>10 — References</a>
          <a href="/briefing/11" onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); navigate('/briefing/11'); }}>11 — Property Portfolio</a>
          {isAdmin && <a href="/admin" onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); navigate('/admin'); }}>👤 User Management</a>}
          {userEmail && (
            <a href="/profile" onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); navigate('/profile'); }}>👤 My Profile</a>
          )}
          {userEmail && (
            <button
              className="btn-sign-out-mobile"
              onClick={handleSignOut}
            >
              🚪 Sign Out
            </button>
          )}
        </aside>
      )}
    </header>
  );
};

export default Header;

